import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import {
  buildImpactReportPreSelectionCoverage,
  IMPACT_REPORT_NO_CAMPAIGNS_BODY,
  IMPACT_REPORT_NO_CAMPAIGNS_TITLE,
  IMPACT_REPORT_NO_DEDICATED_PAGE_BODY,
  IMPACT_REPORT_NO_DEDICATED_PAGE_TITLE,
  IMPACT_REPORT_PRE_SELECTION_BODY,
  IMPACT_REPORT_PRE_SELECTION_TITLE,
  IMPACT_REPORT_SELECTION_EMPTY_TITLE,
} from "@/constants/impactReport";
import type {
  ImpactEmptyStateKind,
  ImpactEmptyStateProps,
  ReportData,
} from "@/interfaces/impactReport";

/**
 * Which of the payload-empty states (everything except `preSelection`) applies to an EMPTY
 * payload. The caller is responsible for having already confirmed `causeData.length === 0` — this
 * function does not re-check it, because `preSelection` is decided before any payload exists and
 * never reaches here.
 *
 * ORDER IS FIXED, and it matters: a selection whose whole spend fell into a cause with no report
 * page is empty AND had giving land somewhere, and "your giving landed, it's just not renderable
 * yet" is the more useful answer than "nothing recorded for this selection" — so
 * `noDedicatedPage` is checked first.
 */
export function resolveImpactEmptyStateKind(
  reportData: ReportData,
): Exclude<ImpactEmptyStateKind, "preSelection" | "noCampaignsInScope"> {
  // The giving DID land somewhere; `causeData` merely has no renderable page for it — exactly the
  // "counts are not lockstep with pages" condition the contract documents.
  if (reportData.summary.mainCause.numCauses > 0) return "noDedicatedPage";

  return "selectionEmpty";
}

/**
 * The report area's THREE states in place of a document or a grid — one per page, never two (see
 * the call sites in `pages/ImpactReport.tsx`, which render this in place of the renderer body for
 * both view modes off the SAME `causeData` the card grid flattens, so the two can never print
 * together).
 *
 *   preSelection    — nothing has been asked yet; the query is disabled. Never says the selection
 *                     has no impact: a client who has merely not chosen a filter has not been
 *                     answered at all.
 *   noDedicatedPage — see `resolveImpactEmptyStateKind`.
 *   selectionEmpty  — a selection was asked for and nothing came back. Names that selection and
 *                     offers ONE action to widen it.
 */
export function ImpactEmptyState(props: ImpactEmptyStateProps) {
  switch (props.kind) {
    case "preSelection": {
      const { preSelectionCoverage } = props;
      return (
        <ImpactEmptyStateLayout
          title={IMPACT_REPORT_PRE_SELECTION_TITLE}
          body={IMPACT_REPORT_PRE_SELECTION_BODY}
          note={
            preSelectionCoverage && preSelectionCoverage.listed > 0
              ? buildImpactReportPreSelectionCoverage(
                  preSelectionCoverage.withRedemptions,
                  preSelectionCoverage.listed,
                )
              : undefined
          }
        />
      );
    }
    case "noCampaignsInScope":
      return (
        <ImpactEmptyStateLayout
          title={IMPACT_REPORT_NO_CAMPAIGNS_TITLE}
          body={IMPACT_REPORT_NO_CAMPAIGNS_BODY}
        />
      );
    case "noDedicatedPage":
      return (
        <ImpactEmptyStateLayout
          title={IMPACT_REPORT_NO_DEDICATED_PAGE_TITLE}
          body={IMPACT_REPORT_NO_DEDICATED_PAGE_BODY}
        />
      );
    case "selectionEmpty": {
      const { selectionEmpty } = props;
      return (
        <ImpactEmptyStateLayout
          title={IMPACT_REPORT_SELECTION_EMPTY_TITLE}
          body={selectionEmpty.message}
          action={
            <Button
              variant="outlined"
              onClick={() => selectionEmpty.onAction()}
              sx={{
                mt: 1,
                borderColor: "var(--color-dark-dust)",
                color: "var(--color-rodeo-black)",
              }}
            >
              {selectionEmpty.actionLabel}
            </Button>
          }
        />
      );
    }
    default: {
      // Exhaustive at compile time: adding a member to `ImpactEmptyStateProps`'s union fails to
      // build until it is answered here.
      const unhandledKind: never = props;
      console.error("ImpactEmptyState: unhandled empty-state kind", unhandledKind);
      return null;
    }
  }
}

interface ImpactEmptyStateLayoutProps {
  title: string;
  body?: string;
  /** An extra line under the body. */
  note?: string;
  /** Rendered last, under the body and note. */
  action?: ReactNode;
}

function ImpactEmptyStateLayout({ title, body, note, action }: ImpactEmptyStateLayoutProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1,
        minHeight: "320px",
      }}
    >
      <Typography
        sx={{
          fontSize: "18px",
          fontWeight: 700,
          color: "var(--color-rodeo-black)",
        }}
      >
        {title}
      </Typography>
      {body && (
        <Typography
          sx={{
            fontSize: "14px",
            color: "var(--color-graphite)",
            maxWidth: "420px",
          }}
        >
          {body}
        </Typography>
      )}
      {note && (
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: "var(--color-rodeo-black)",
            maxWidth: "420px",
          }}
        >
          {note}
        </Typography>
      )}
      {action}
    </Box>
  );
}
